import React from "react";
import { useParams, Link } from "react-router-dom";
import moment from "moment";

import PageTitle from "../../../layouts/PageTitle";
import { useGetEquipmentDetailQuery, useGetEquipmentExpensesQuery } from "../../../../store/appApi";

const STATUS_BADGE = {
  Active: "success",
  Inactive: "secondary",
  "Under Service": "warning",
  Breakdown: "danger",
  Relocating: "info",
};

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const InfoRow = ({ label, value }) => (
  <div className="col-md-4 mb-2">
    <span className="text-muted small d-block">{label}</span>
    <span className="fw-semibold">{value || "—"}</span>
  </div>
);

const getAlertInfo = (equipment) => {
  if (!equipment) return null;

  if (equipment.serviceMode === "Hour-Based" && equipment.alertIntervalMonths) {
    const totalHours = (equipment.serviceHistory || [])
      .filter((s) => s.serviceType === "Hour-Based")
      .reduce((sum, s) => sum + (s.metricValue || 0), 0);
    if (totalHours >= equipment.alertIntervalMonths) {
      return { variant: "danger", msg: `Hours exceeded: ${totalHours} hrs logged (limit: ${equipment.alertIntervalMonths} hrs)` };
    }
    if (totalHours >= equipment.alertIntervalMonths * 0.9) {
      return { variant: "warning", msg: `Approaching hour limit: ${totalHours} / ${equipment.alertIntervalMonths} hrs` };
    }
  }

  if (equipment.serviceMode === "Fuel Consumption" && equipment.alertFuelThreshold) {
    const consumed = (equipment.serviceHistory || [])
      .filter((s) => s.serviceType === "Fuel Consumption")
      .reduce((sum, s) => sum + (s.metricValue || 0), 0);
    if (consumed >= equipment.alertFuelThreshold) {
      return { variant: "danger", msg: `Fuel threshold reached: ${consumed} L consumed (limit: ${equipment.alertFuelThreshold} L)` };
    }
    if (consumed >= equipment.alertFuelThreshold * 0.9) {
      return { variant: "warning", msg: `Approaching fuel threshold: ${consumed} / ${equipment.alertFuelThreshold} L` };
    }
  }

  return null;
};

const EquipmentDetail = () => {
  const { id } = useParams();
  const { data, isLoading, isError } = useGetEquipmentDetailQuery(id);
  const { data: expensesData } = useGetEquipmentExpensesQuery(id);

  const equipment = data?.equipment || null;
  const totals = data?.totals || {};
  const financeExpenses = expensesData?.expenses || [];
  const financeTotal = expensesData?.total || 0;

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (isError || !equipment) {
    return (
      <div className="alert alert-danger m-4">
        Failed to load equipment.{" "}
        <Link to="/progress/equipement/list">Back to list</Link>
      </div>
    );
  }

  const totalPerRecord = (s) => (s.serviceCost || 0) + (s.maintenanceCost || 0);
  const alertInfo = getAlertInfo(equipment);

  return (
    <>
      <PageTitle activeMenu="Equipment Detail" motherMenu="Daily Progress / Equipment" />

      {alertInfo && (
        <div className={`alert alert-${alertInfo.variant} d-flex align-items-center mb-3`} role="alert">
          <i className={`la la-exclamation-triangle me-2 fs-4 text-${alertInfo.variant}`} />
          <strong className="me-1">Service Alert:</strong> {alertInfo.msg}
        </div>
      )}

      <div className="mb-3 d-flex gap-2 flex-wrap">
        <Link to="/progress/equipement/list" className="btn btn-sm btn-outline-secondary">
          &larr; Back to List
        </Link>
        <Link to={`/progress/equipement/${id}/detail`} className="btn btn-sm btn-primary">
          + Log Service
        </Link>
        <Link to="/progress/equipement/relocate" className="btn btn-sm btn-outline-warning">
          Relocate Asset
        </Link>
      </div>

      {/* ── Core Equipment Details ─────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="la la-cogs me-2 text-primary" />
            {equipment.eqpName}
          </h5>
          <span
            className={`badge light badge-${STATUS_BADGE[equipment.eqpStatus] || "secondary"}`}
            style={{ fontSize: "0.9rem", padding: "0.35rem 0.75rem" }}
          >
            {equipment.eqpStatus || "Active"}
          </span>
        </div>
        <div className="card-body">
          <div className="row">
            <InfoRow label="Equipment Type"   value={equipment.eqpType} />
            <InfoRow label="Number / Reg."    value={equipment.eqpNumber} />
            <InfoRow label="Make"             value={equipment.eqpMake} />
            <InfoRow label="Model"            value={equipment.eqpModel} />
            <InfoRow label="Year"             value={equipment.eqpYear} />
            <InfoRow label="Condition"        value={equipment.eqpCondition} />
            <InfoRow label="Project"          value={equipment.projectId?.projectName} />
            <InfoRow label="Added By"         value={equipment.addedBy?.name} />
            <InfoRow label="Date Added"       value={equipment.created ? moment(equipment.created).format("DD MMM YYYY") : null} />
            <InfoRow
              label="Last Serviced"
              value={equipment.eqpLastServiced ? moment(equipment.eqpLastServiced).format("DD MMM YYYY") : null}
            />
            <InfoRow
              label="Next Service Due"
              value={equipment.eqpNextServiceDue ? moment(equipment.eqpNextServiceDue).format("DD MMM YYYY") : null}
            />
            <InfoRow
              label="Warranty Expiry"
              value={equipment.eqpWarrantyExpiry ? moment(equipment.eqpWarrantyExpiry).format("DD MMM YYYY") : null}
            />
            <InfoRow label="Service Type"     value={equipment.eqpServiceType} />
            <InfoRow label="Location / Shift" value={equipment.eqpLocationShift} />
            <div className="col-md-4 mb-2">
              <span className="text-muted small d-block">Service Mode</span>
              {equipment.serviceMode ? (
                <span className={`badge light ${equipment.serviceMode === "Hour-Based" ? "badge-info" : "badge-warning"}`}>
                  {equipment.serviceMode === "Hour-Based" ? "⏱ Hour-Based" : "⛽ Fuel-Based"}
                </span>
              ) : "—"}
            </div>
          </div>
          {equipment.eqpNotes && (
            <div className="mt-2 p-3 bg-light rounded">
              <span className="text-muted small d-block">Notes / Remarks</span>
              <span>{equipment.eqpNotes}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Cost KPIs ────────────────────────────────────────────────────────── */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card border-primary text-center">
            <div className="card-body">
              <p className="mb-1 text-muted small">Total Service Cost</p>
              <h3 className="mb-0 text-primary font-weight-bold">₹{fmt(totals.totalServiceCost)}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-warning text-center">
            <div className="card-body">
              <p className="mb-1 text-muted small">Total Financed Expenses</p>
              <h3 className="mb-0 text-warning font-weight-bold">₹{fmt(financeTotal)}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-danger text-center">
            <div className="card-body">
              <p className="mb-1 text-muted small">Combined Lifecycle Cost</p>
              <h3 className="mb-0 text-danger font-weight-bold">₹{fmt((totals.totalServiceCost || 0) + financeTotal)}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* ── Service History ───────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="la la-wrench me-2 text-primary" />
            Service History
          </h5>
          <span className="badge badge-primary">
            {(equipment.serviceHistory || []).length} records
          </span>
        </div>
        <div className="card-body">
          {!equipment.serviceHistory || equipment.serviceHistory.length === 0 ? (
            <p className="text-muted text-center py-3">No service records yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover verticle-middle">
                <thead className="thead-dark">
                  <tr>
                    <th>#</th>
                    <th>Last Serviced</th>
                    <th>Location / Site</th>
                    <th>Service Type</th>
                    <th>Metric</th>
                    <th>Service Cost</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {equipment.serviceHistory.map((s, i) => (
                    <tr key={s._id || i}>
                      <td>{i + 1}</td>
                      <td className="small">
                        {s.date ? moment(s.date).format("DD MMM YYYY") : "—"}
                      </td>
                      <td className="small">{s.eqpLocationShift || "—"}</td>
                      <td>
                        <span className="badge badge-primary light">
                          {s.serviceType || "—"}
                        </span>
                      </td>
                      <td className="small">
                        {s.metricValue != null ? fmt(s.metricValue) : "—"}
                      </td>
                      <td className="small text-success fw-semibold">
                        ₹{fmt(s.serviceCost)}
                      </td>
                      <td className="small text-muted">{s.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Finance Expenses ──────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="la la-rupee me-2 text-warning" />
            Finance Expenses
          </h5>
          <span className="badge badge-warning">{financeExpenses.length} entries · ₹{fmt(financeTotal)}</span>
        </div>
        <div className="card-body">
          {financeExpenses.length === 0 ? (
            <p className="text-muted text-center py-3">No finance records linked yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover verticle-middle">
                <thead className="thead-dark">
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Added By</th>
                  </tr>
                </thead>
                <tbody>
                  {financeExpenses.map((e, i) => (
                    <tr key={e._id || i}>
                      <td>{i + 1}</td>
                      <td className="small">{e.created ? moment(e.created).format("DD MMM YYYY") : "—"}</td>
                      <td className="small text-success fw-semibold">₹{fmt(e.amount)}</td>
                      <td className="small text-muted">{e.desc || "—"}</td>
                      <td className="small">{e.userId?.name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Location / Relocation History ──────────────────────────────────────── */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="la la-map-marker me-2 text-info" />
            Location History
          </h5>
          <span className="badge badge-info">
            {(equipment.relocationHistory || []).length} moves
          </span>
        </div>
        <div className="card-body">
          {!equipment.relocationHistory || equipment.relocationHistory.length === 0 ? (
            <p className="text-muted text-center py-3">No relocations recorded.</p>
          ) : (
            <div className="timeline">
              {equipment.relocationHistory.map((r, i) => (
                <div key={r._id || i} className="d-flex mb-4">
                  <div className="mr-3 d-flex flex-column align-items-center" style={{ minWidth: 32 }}>
                    <div
                      className="rounded-circle bg-info d-flex align-items-center justify-content-center text-white"
                      style={{ width: 32, height: 32, fontSize: 13, flexShrink: 0 }}
                    >
                      {i + 1}
                    </div>
                    {i < equipment.relocationHistory.length - 1 && (
                      <div style={{ width: 2, flexGrow: 1, background: "#dee2e6", minHeight: 24 }} />
                    )}
                  </div>
                  <div className="card flex-grow-1" style={{ border: "1px solid #dee2e6" }}>
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{r.fromProject?.projectName || "N/A"}</strong>
                          {" → "}
                          <strong>{r.toProject?.projectName || "N/A"}</strong>
                        </div>
                        <small className="text-muted">
                          {r.transferDate ? moment(r.transferDate).format("DD MMM YYYY") : "—"}
                        </small>
                      </div>
                      {r.approvedBy && (
                        <p className="mb-0 mt-1 text-muted small">
                          Approved by: <strong>{r.approvedBy}</strong>
                        </p>
                      )}
                      {r.transferNotes && (
                        <p className="mb-0 mt-1 text-muted small">{r.transferNotes}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EquipmentDetail;
